import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import type { TFunction } from "i18next";
import { useMemo, useState, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  shouldUseTabbedResponse,
  type PipelineVerboseStep,
} from "@/features/chat/lib/turnStream";
import { CardsPanel } from "@/features/chat/ui/CardsPanel";
import { ChartPanel } from "@/features/chat/ui/ChartPanel";
import { ChatTurnFeedbackButtons } from "@/features/chat/ui/ChatTurnFeedbackButtons";
import type {
  AnswerConfidence,
  ChatChartCollection,
  ChatTabularData,
  ChatTurnFeedback,
  DocumentAgreement,
  DocumentCitation,
  DocumentConflict,
  DocumentSourceRef,
  GeneratedQuery,
  GeneralizedChartDefinition,
} from "@/features/chat/model/chatTypes";
import { JsonCodeMirror } from "@/shared/ui/JsonCodeMirror";
import { SqlCodeMirror } from "@/shared/ui/SqlCodeMirror";
import { useSqlDialect } from "@/shared/hooks/useSqlDialect";

type ResponseTabId = "insights" | "table" | "chart" | "cards" | "sql" | "howGenerated";

type ChatAssistantResponseProps = {
  text: string;
  summary?: string | null;
  insights?: string[] | null;
  why?: string | null;
  action?: string | null;
  businessImpact?: string | null;
  verboseLog: PipelineVerboseStep[];
  followUpQuestions: string[];
  tabularData?: ChatTabularData | null;
  chartData?: ChatChartCollection | null;
  generatedQueries?: GeneratedQuery[] | null;
  answerConfidence?: AnswerConfidence | null;
  documentAgreement?: DocumentAgreement | null;
  documentConflicts?: DocumentConflict[];
  documentSources?: string[];
  documentSourceRefs?: DocumentSourceRef[];
  isStreaming?: boolean;
  isTerminated?: boolean;
  onFollowUpClick?: (question: string) => void;
  onAddChartToApp?: (chart: GeneralizedChartDefinition) => void;
  canAddChartToApp?: boolean;
  onAddTableToApp?: () => void;
  canAddTableToApp?: boolean;
  onAddCardsToApp?: () => void;
  canAddCardsToApp?: boolean;
  domainId?: number | null;
  /** Identifies the turn for feedback; omitted (or still streaming) hides the thumbs. */
  turnId?: string | null;
  feedback?: ChatTurnFeedback | null;
};

export function ChatAssistantResponse({
  text,
  summary = null,
  insights = null,
  why = null,
  action = null,
  businessImpact = null,
  verboseLog,
  followUpQuestions,
  tabularData = null,
  chartData = null,
  generatedQueries = null,
  answerConfidence = null,
  documentAgreement = null,
  documentConflicts = [],
  documentSources = [],
  documentSourceRefs = [],
  isStreaming = false,
  isTerminated = false,
  onFollowUpClick,
  onAddChartToApp,
  canAddChartToApp = false,
  onAddTableToApp,
  canAddTableToApp = false,
  onAddCardsToApp,
  canAddCardsToApp = false,
  domainId = null,
  turnId = null,
  feedback = null,
}: ChatAssistantResponseProps) {
  const { t } = useTranslation("chat");
  const displayText = text.trim() || (isStreaming ? "" : t("assistantPlaceholder"));

  const useTabbed = shouldUseTabbedResponse({
    text: displayText,
    verboseLog,
    followUpQuestions,
  }) ||
    Boolean(tabularData) ||
    Boolean(chartData?.charts?.length) ||
    Boolean(chartData?.cards?.length) ||
    Boolean(generatedQueries?.length) ||
    isTerminated;

  const visibleTabs = useMemo(() => {
    const tabs: Array<{ id: ResponseTabId; label: string; icon: ReactElement }> = [
      {
        id: "insights",
        label: t("response.tabs.insights"),
        icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />,
      },
    ];
    if (tabularData && tabularData.columns.length > 0) {
      tabs.push({
        id: "table",
        label: t("response.tabs.table"),
        icon: <TableChartOutlinedIcon sx={{ fontSize: 16 }} />,
      });
    }
    if (chartData && chartData.charts.length > 0) {
      tabs.push({
        id: "chart",
        label: t("response.tabs.chart"),
        icon: <BarChartOutlinedIcon sx={{ fontSize: 16 }} />,
      });
    }
    if (chartData && chartData.cards && chartData.cards.length > 0) {
      tabs.push({
        id: "cards",
        label: t("response.tabs.cards"),
        icon: <ViewModuleOutlinedIcon sx={{ fontSize: 16 }} />,
      });
    }
    if (generatedQueries && generatedQueries.length > 0) {
      tabs.push({
        id: "sql",
        label: t("response.tabs.sql"),
        icon: <DataObjectOutlinedIcon sx={{ fontSize: 16 }} />,
      });
    }
    if (verboseLog.length > 0) {
      tabs.push({
        id: "howGenerated",
        label: t("response.tabs.howGenerated"),
        icon: <BuildOutlinedIcon sx={{ fontSize: 16 }} />,
      });
    }
    return tabs;
  }, [chartData, generatedQueries, t, tabularData, verboseLog.length]);

  const [activeTab, setActiveTab] = useState<ResponseTabId>("insights");

  const effectiveTab = visibleTabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : "insights";

  // Feedback needs a real, server-assigned turn id: while a turn is still streaming, `turnId`
  // is briefly the client-generated placeholder created before the request resolved.
  const showFeedback = Boolean(turnId) && !isStreaming;

  if (!useTabbed) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            width: "100%",
          }}
        >
          <AvatarIcon />
          <MessageBubble>
            {isTerminated ? (
              <TerminatedInsightsMessage />
            ) : (
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {displayText}
                {isStreaming ? <StreamingCursor /> : null}
              </Typography>
            )}
          </MessageBubble>
        </Box>
        {showFeedback && turnId ? (
          <Stack direction="row" justifyContent="flex-end">
            <ChatTurnFeedbackButtons turnId={turnId} feedback={feedback} />
          </Stack>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        width: "100%",
      }}
    >
      <AvatarIcon />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          maxWidth: "100%",
          borderRadius: 2,
          border: "1px solid var(--theme-border)",
          bgcolor: "var(--theme-surface)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid var(--theme-border)",
          }}
        >
          <Tabs
            value={effectiveTab}
            onChange={(_, value: ResponseTabId) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 40,
              px: 1,
              "& .MuiTabs-indicator": {
                backgroundColor: "var(--theme-primary)",
              },
              "& .MuiTab-root": {
                minHeight: 40,
                minWidth: "auto",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8125rem",
                color: "var(--theme-text-secondary)",
                gap: 0.5,
                "&.Mui-selected": {
                  color: "var(--theme-primary)",
                },
              },
            }}
          >
            {visibleTabs.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
          {showFeedback && turnId ? (
            <Box sx={{ flexShrink: 0, pr: 1 }}>
              <ChatTurnFeedbackButtons turnId={turnId} feedback={feedback} />
            </Box>
          ) : null}
        </Box>

        <Box sx={{ px: 2, py: 1.5 }}>
          {effectiveTab === "insights" ? (
            isTerminated ? (
              <TerminatedInsightsMessage />
            ) : (
              <InsightsPanel
                displayText={displayText}
                summary={summary}
                insights={insights}
                why={why}
                action={action}
                businessImpact={businessImpact}
                followUpQuestions={followUpQuestions}
                answerConfidence={answerConfidence}
                documentAgreement={documentAgreement}
                documentConflicts={documentConflicts}
                documentSources={documentSources}
                documentSourceRefs={documentSourceRefs}
                isStreaming={isStreaming}
                onFollowUpClick={onFollowUpClick}
              />
            )
          ) : null}

          {effectiveTab === "table" && tabularData ? (
            <DataTable
              tabularData={tabularData}
              onAddToApp={canAddTableToApp ? onAddTableToApp : undefined}
            />
          ) : null}

          {effectiveTab === "chart" && chartData ? (
            <ChartPanel
              charts={chartData.charts}
              data={chartData.data}
              onAddToApp={canAddChartToApp ? onAddChartToApp : undefined}
            />
          ) : null}

          {effectiveTab === "cards" && chartData?.cards ? (
            <CardsPanel
              cards={chartData.cards}
              onAddToApp={canAddCardsToApp ? onAddCardsToApp : undefined}
            />
          ) : null}

          {effectiveTab === "sql" && generatedQueries ? (
            <SqlQueriesPanel queries={generatedQueries} domainId={domainId} />
          ) : null}

          {effectiveTab === "howGenerated" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {verboseLog.map((step) =>
                step.eventType === "llm_call" ? (
                  <LlmCallStep key={`${step.stepIndex}-${step.eventType}`} step={step} />
                ) : step.eventType === "rag_retrieval" || step.eventType === "graph_search" ? (
                  <RetrievalStep key={`${step.stepIndex}-${step.eventType}`} step={step} />
                ) : (
                  <Box key={`${step.stepIndex}-${step.eventType}`}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {step.stepTitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "pre-wrap", color: "var(--theme-text-secondary)", mt: 0.5 }}
                    >
                      {step.detail}
                    </Typography>
                  </Box>
                ),
              )}
              {isTerminated ? <TerminatedHowGeneratedNotice /> : null}
            </Box>
          ) : null}

        </Box>
      </Box>
    </Box>
  );
}

const ACCORDION_SX = {
  bgcolor: "transparent",
  boxShadow: "none",
  border: "1px solid var(--theme-border)",
  borderRadius: "6px !important",
  "&:before": { display: "none" },
  "&.Mui-expanded": { margin: 0 },
} as const;

const ACCORDION_SUMMARY_SX = {
  minHeight: 36,
  px: 1.5,
  "&.Mui-expanded": { minHeight: 36 },
  "& .MuiAccordionSummary-content": { my: 0.75 },
} as const;

function parseJsonStructure(value: string): Record<string, unknown> | unknown[] | null {
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed !== null && typeof parsed === "object"
      ? (parsed as Record<string, unknown> | unknown[])
      : null;
  } catch {
    return null;
  }
}

function noop() {
  // JsonCodeMirror requires onChange even when readOnly.
}

function RawIoStep({
  stepTitle,
  requestText,
  responseText,
  responseIsJson,
}: {
  stepTitle: string;
  requestText: string;
  responseText: string;
  responseIsJson: boolean;
}) {
  const { t } = useTranslation("chat");

  return (
    <Accordion disableGutters sx={ACCORDION_SX}>
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={ACCORDION_SUMMARY_SX}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {stepTitle}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "var(--theme-text-secondary)" }}>
          {t("response.howGenerated.request")}
        </Typography>
        <JsonCodeMirror value={requestText} onChange={noop} readOnly maxHeight="200px" />
        <Typography variant="caption" sx={{ fontWeight: 600, color: "var(--theme-text-secondary)" }}>
          {t("response.howGenerated.response")}
        </Typography>
        {responseIsJson ? (
          <JsonCodeMirror value={responseText} onChange={noop} readOnly maxHeight="200px" />
        ) : (
          <SqlCodeMirror value={responseText} readOnly maxHeight="200px" />
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function LlmCallStep({ step }: { step: PipelineVerboseStep }) {
  const requestText = useMemo(() => {
    const messages = step.messages ?? [];
    const formattedMessages = messages.map((message) => {
      const parsedContent = parseJsonStructure(message.content);
      return parsedContent !== null ? { ...message, content: parsedContent } : message;
    });
    return JSON.stringify(formattedMessages, null, 2);
  }, [step.messages]);

  const response = useMemo(() => {
    const rawResponse = step.rawResponse ?? "";
    const parsedResponse = parseJsonStructure(rawResponse);
    return parsedResponse !== null
      ? { text: JSON.stringify(parsedResponse, null, 2), isJson: true }
      : { text: rawResponse, isJson: false };
  }, [step.rawResponse]);

  return (
    <RawIoStep
      stepTitle={step.stepTitle}
      requestText={requestText}
      responseText={response.text}
      responseIsJson={response.isJson}
    />
  );
}

function RetrievalStep({ step }: { step: PipelineVerboseStep }) {
  const requestText = useMemo(() => JSON.stringify(step.input ?? {}, null, 2), [step.input]);
  const responseText = useMemo(() => JSON.stringify(step.output ?? {}, null, 2), [step.output]);

  return (
    <RawIoStep
      stepTitle={step.stepTitle}
      requestText={requestText}
      responseText={responseText}
      responseIsJson
    />
  );
}

function InsightsPanel({
  displayText,
  summary,
  insights,
  why,
  action,
  businessImpact,
  followUpQuestions,
  answerConfidence,
  documentAgreement,
  documentConflicts,
  documentSources,
  documentSourceRefs,
  isStreaming,
  onFollowUpClick,
}: {
  displayText: string;
  summary: string | null;
  insights: string[] | null;
  why: string | null;
  action: string | null;
  businessImpact: string | null;
  followUpQuestions: string[];
  answerConfidence?: AnswerConfidence | null;
  documentAgreement?: DocumentAgreement | null;
  documentConflicts?: DocumentConflict[];
  documentSources?: string[];
  documentSourceRefs?: DocumentSourceRef[];
  isStreaming: boolean;
  onFollowUpClick?: (question: string) => void;
}) {
  const { t } = useTranslation("chat");
  const isStructured = !isStreaming && insights !== null && insights.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Rendered outside the structured branch: a disagreement between the warehouse and a
          document matters whether or not the answer came back in the structured shape. */}
      {!isStreaming ? (
        <DocumentEvidence
          agreement={documentAgreement ?? null}
          conflicts={documentConflicts ?? []}
          sources={documentSources ?? []}
          sourceRefs={documentSourceRefs ?? []}
        />
      ) : null}

      {isStructured ? (
        <>
          {answerConfidence ? <AnswerConfidenceBadge confidence={answerConfidence} /> : null}

          {summary ? (
            <Typography variant="body2" sx={{ color: "var(--theme-text-primary)" }}>
              {summary}
            </Typography>
          ) : null}

          <Accordion defaultExpanded disableGutters sx={ACCORDION_SX}>
            <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={ACCORDION_SUMMARY_SX}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("response.sections.insights")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
              <Box component="ul" sx={{ pl: 2, m: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
                {insights.map((item, index) => (
                  <Typography key={index} component="li" variant="body2">
                    {item}
                  </Typography>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          {why ? (
            <Accordion disableGutters sx={ACCORDION_SX}>
              <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={ACCORDION_SUMMARY_SX}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("response.sections.why")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                <Typography variant="body2">{why}</Typography>
              </AccordionDetails>
            </Accordion>
          ) : null}

          {action ? (
            <Accordion disableGutters sx={ACCORDION_SX}>
              <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={ACCORDION_SUMMARY_SX}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("response.sections.action")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                <Typography variant="body2">{action}</Typography>
              </AccordionDetails>
            </Accordion>
          ) : null}

          {businessImpact ? (
            <Accordion disableGutters sx={ACCORDION_SX}>
              <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={ACCORDION_SUMMARY_SX}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("response.sections.businessImpact")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                <Typography variant="body2">{businessImpact}</Typography>
              </AccordionDetails>
            </Accordion>
          ) : null}
        </>
      ) : (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "var(--theme-text-primary)" }}>
          {displayText}
          {isStreaming ? <StreamingCursor /> : null}
        </Typography>
      )}

      {!isStreaming && followUpQuestions.length > 0 ? (
        <Box sx={{ borderTop: "1px solid var(--theme-border)", pt: 1.5 }}>
          <Typography
            variant="caption"
            sx={{ color: "var(--theme-text-secondary)", mb: 1, display: "block" }}
          >
            {t("response.followUps.suggested")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {followUpQuestions.map((question) => (
              <Chip
                key={question}
                label={question}
                size="small"
                clickable={Boolean(onFollowUpClick)}
                onClick={onFollowUpClick ? () => onFollowUpClick(question) : undefined}
                sx={{
                  height: "auto",
                  py: 0.5,
                  "& .MuiChip-label": { whiteSpace: "normal", display: "block" },
                }}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

const DOCUMENT_AGREEMENT_COLOR_TOKEN: Partial<Record<DocumentAgreement, string>> = {
  supports: "var(--theme-success)",
  complements: "var(--theme-info, var(--theme-primary))",
  conflicts: "var(--theme-warning)",
  document_only: "var(--theme-info, var(--theme-primary))",
};

/**
 * Document provenance for an answer: which uploaded files it drew on, and — when the warehouse
 * and a document disagree — both values side by side. The disagreement is shown, never resolved:
 * warehouse data stays authoritative, but hiding the difference would be the wrong call.
 *
 * `sourceRefs` carries page numbers and preview locations; `sources` is the bare filename list the
 * pipeline has always written. Refs are preferred when present and the filenames are the fallback,
 * so a turn answered before the refs existed still shows where it read from.
 */
function DocumentEvidence({
  agreement,
  conflicts,
  sources,
  sourceRefs,
}: {
  agreement: DocumentAgreement | null;
  conflicts: DocumentConflict[];
  sources: string[];
  sourceRefs: DocumentSourceRef[];
}) {
  const { t } = useTranslation("chat");
  const showAgreement = agreement !== null && agreement !== "not_relevant";
  // Split rather than merged, because the two say different things to a reader. A same-period
  // disagreement means two sources measured one thing and got two answers, so neither number is
  // safe on its own; a different-period one explains itself. Both panels use the warning palette —
  // they are told apart by their heading and icon, not by colour, so a turn carrying both does not
  // read as two severities of the same finding.
  const ambiguousConflicts = conflicts.filter((conflict) => conflict.samePeriod);
  const datedConflicts = conflicts.filter((conflict) => !conflict.samePeriod);
  // Memoised for its identity, not its cost: the preview strip fetches on this list changing, and a
  // fresh array every render would refetch on every render — including the ones its own fetch causes.
  const effectiveRefs = useMemo<DocumentSourceRef[]>(
    () =>
      sourceRefs.length > 0
        ? sourceRefs
        : sources.map((source) => ({ sourceFile: source, isImage: false })),
    [sourceRefs, sources],
  );

  if (!showAgreement && conflicts.length === 0 && effectiveRefs.length === 0) {
    return null;
  }

  const agreementColor = agreement
    ? DOCUMENT_AGREEMENT_COLOR_TOKEN[agreement]
    : undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {showAgreement && agreementColor ? (
        <Box sx={{ display: "flex" }}>
          <Chip
            size="small"
            icon={
              <DescriptionOutlinedIcon
                sx={{ fontSize: 14, color: `${agreementColor} !important` }}
              />
            }
            label={t(`response.documents.agreement.${agreement}`)}
            sx={{
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 600,
              bgcolor: `color-mix(in srgb, ${agreementColor} 12%, transparent)`,
              color: agreementColor,
              border: `1px solid color-mix(in srgb, ${agreementColor} 35%, transparent)`,
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        </Box>
      ) : null}

      {ambiguousConflicts.length > 0 ? (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: "color-mix(in srgb, var(--theme-warning, #ed6c02) 12%, transparent)",
            border: "1px solid color-mix(in srgb, var(--theme-warning, #ed6c02) 35%, transparent)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <ErrorOutlineOutlinedIcon
              sx={{ fontSize: 20, color: "var(--theme-warning, #ed6c02)", mt: 0.25 }}
              aria-hidden
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("response.documents.ambiguityTitle")}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "var(--theme-text-secondary)", display: "block", mt: 0.25 }}
              >
                {t("response.documents.ambiguityHelp")}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.5 }}>
            {ambiguousConflicts.map((conflict) => (
              <Box key={`ambiguous-${conflict.subject}-${conflict.documentValue}`}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {conflict.subject}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                  {t("response.documents.dataSays")}: {conflict.structuredValue}
                  {conflict.structuredSource ? ` (${conflict.structuredSource})` : ""}
                </Typography>
                <Typography variant="body2">
                  {t("response.documents.documentsSay")}: {conflict.documentValue}
                  {formatCitations(conflict.documentSources, t)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "var(--theme-text-secondary)", display: "block", mt: 0.25 }}
                >
                  {conflict.likelyCause
                    ? `${t("response.documents.likelyCause")}: ${conflict.likelyCause}`
                    : t("response.documents.samePeriodNoCause")}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      {datedConflicts.length > 0 ? (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: "color-mix(in srgb, var(--theme-warning, #ed6c02) 12%, transparent)",
            border: "1px solid color-mix(in srgb, var(--theme-warning, #ed6c02) 35%, transparent)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <ReportProblemOutlinedIcon
              sx={{ fontSize: 20, color: "var(--theme-warning, #ed6c02)", mt: 0.25 }}
              aria-hidden
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("response.documents.conflictTitle")}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "var(--theme-text-secondary)", display: "block", mt: 0.25 }}
              >
                {t("response.documents.conflictHelp")}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.5 }}>
            {datedConflicts.map((conflict) => (
              <Box key={`${conflict.subject}-${conflict.documentValue}`}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {conflict.subject}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                  {t("response.documents.dataSays")}: {conflict.structuredValue}
                  {conflict.structuredSource ? ` (${conflict.structuredSource})` : ""}
                </Typography>
                <Typography variant="body2">
                  {t("response.documents.documentsSay")}: {conflict.documentValue}
                  {formatCitations(conflict.documentSources, t)}
                </Typography>
                {conflict.likelyCause ? (
                  <Typography
                    variant="caption"
                    sx={{ color: "var(--theme-text-secondary)", display: "block", mt: 0.25 }}
                  >
                    {t("response.documents.likelyCause")}: {conflict.likelyCause}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      {effectiveRefs.length > 0 ? (
        <Box>
          <Typography
            variant="caption"
            sx={{ color: "var(--theme-text-secondary)", display: "block", mb: 0.5 }}
          >
            {t("response.documents.sourcesTitle")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {effectiveRefs.map((ref) => (
              <Chip
                key={`${ref.sourceFile}-${ref.page ?? "file"}`}
                size="small"
                icon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />}
                label={sourceRefLabel(ref, t)}
                sx={{ height: 22, fontSize: "0.7rem", "& .MuiChip-label": { px: 0.75 } }}
              />
            ))}
          </Box>
          <DocumentPagePreviews refs={effectiveRefs} />
        </Box>
      ) : null}
    </Box>
  );
}

/** ` (file.pdf, p. 3; other.pdf)`, or `""` for an uncited value — appended after a value inline. */
function formatCitations(
  citations: DocumentCitation[],
  t: TFunction<"chat">,
): string {
  if (citations.length === 0) {
    return "";
  }
  const rendered = citations.map((citation) =>
    citation.page
      ? `${citation.sourceFile}, ${t("response.documents.page", { page: citation.page })}`
      : citation.sourceFile,
  );
  return ` (${rendered.join("; ")})`;
}

/** `file.pdf · p. 3`, or just the filename when the parse produced no page number. */
function sourceRefLabel(sourceRef: DocumentSourceRef, t: TFunction<"chat">): string {
  return sourceRef.page
    ? `${sourceRef.sourceFile} · ${t("response.documents.page", { page: sourceRef.page })}`
    : sourceRef.sourceFile;
}

/**
 * The cited pages that were retrieved as images — slides and figures whose numbers are in the page
 * and nowhere in the document's text, so the page itself is the only way to check a claim against
 * what was actually on it.
 *
 * Rendered inline from the `data:` URI the turn carries, and clicking one enlarges it in place. No
 * link to the file and no download: the point is to see the passage the answer was read off, and
 * fetching a whole PDF to find it again is not that.
 */
function DocumentPagePreviews({ refs }: { refs: DocumentSourceRef[] }) {
  const { t } = useTranslation("chat");
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const previewable = refs.filter((ref) => Boolean(ref.previewImage));
  if (previewable.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        sx={{ color: "var(--theme-text-secondary)", display: "block", mb: 0.5 }}
      >
        {t("response.documents.pagePreviewsTitle")}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start" }}>
        {previewable.map((ref) => {
          const key = `${ref.sourceFile}-${ref.page ?? "file"}`;
          const isExpanded = expandedKey === key;
          const label = sourceRefLabel(ref, t);
          return (
            <Tooltip
              key={key}
              title={
                isExpanded ? t("response.documents.collapsePage") : t("response.documents.expandPage")
              }
            >
              <Box
                component="button"
                type="button"
                aria-label={label}
                aria-expanded={isExpanded}
                onClick={() => setExpandedKey(isExpanded ? null : key)}
                sx={{
                  p: 0,
                  border: "1px solid var(--theme-border)",
                  borderRadius: 1,
                  overflow: "hidden",
                  lineHeight: 0,
                  cursor: "pointer",
                  bgcolor: "transparent",
                  display: "block",
                }}
              >
                <Box
                  component="img"
                  src={ref.previewImage ?? undefined}
                  alt={label}
                  sx={{
                    display: "block",
                    width: isExpanded ? "100%" : 200,
                    maxWidth: isExpanded ? 720 : 200,
                    maxHeight: isExpanded ? "none" : 150,
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

const CONFIDENCE_LEVEL_COLOR_TOKEN: Record<AnswerConfidence["level"], string> = {
  high: "var(--theme-success)",
  medium: "var(--theme-warning)",
  low: "var(--theme-error)",
};

function AnswerConfidenceBadge({ confidence }: { confidence: AnswerConfidence }) {
  const { t } = useTranslation("chat");
  const color = CONFIDENCE_LEVEL_COLOR_TOKEN[confidence.level];
  const label = t(`response.confidence.levels.${confidence.level}`, {
    score: Math.round(confidence.score * 100),
  });
  const tooltip =
    confidence.signals.length > 0
      ? confidence.signals.join(" · ")
      : t("response.confidence.noSignals");

  return (
    <Box sx={{ display: "flex" }}>
      <Tooltip title={tooltip}>
        <Chip
          size="small"
          icon={<VerifiedOutlinedIcon sx={{ fontSize: 14, color: `${color} !important` }} />}
          label={label}
          sx={{
            height: 22,
            fontSize: "0.7rem",
            fontWeight: 600,
            bgcolor: `color-mix(in srgb, ${color} 12%, transparent)`,
            color,
            border: `1px solid color-mix(in srgb, ${color} 35%, transparent)`,
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      </Tooltip>
    </Box>
  );
}

function TerminatedInsightsMessage() {
  const { t } = useTranslation("chat");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        p: 1.5,
        borderRadius: 1,
        bgcolor: "color-mix(in srgb, var(--theme-warning, #ed6c02) 12%, transparent)",
        border: "1px solid color-mix(in srgb, var(--theme-warning, #ed6c02) 35%, transparent)",
      }}
    >
      <StopCircleOutlinedIcon
        sx={{ fontSize: 20, color: "var(--theme-warning, #ed6c02)", mt: 0.25 }}
        aria-hidden
      />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: "var(--theme-text-primary)" }}>
          {t("terminated.title")}
        </Typography>
        <Typography variant="body2" sx={{ color: "var(--theme-text-secondary)", mt: 0.5 }}>
          {t("terminated.message")}
        </Typography>
      </Box>
    </Box>
  );
}

function TerminatedHowGeneratedNotice() {
  const { t } = useTranslation("chat");

  return (
    <Box
      sx={{
        mt: 0.5,
        p: 1.5,
        borderRadius: 1,
        bgcolor: "color-mix(in srgb, var(--theme-warning, #ed6c02) 12%, transparent)",
        border: "1px solid color-mix(in srgb, var(--theme-warning, #ed6c02) 35%, transparent)",
      }}
    >
      <Typography variant="body2" sx={{ color: "var(--theme-text-secondary)" }}>
        {t("terminated.howGenerated")}
      </Typography>
    </Box>
  );
}

function DataTable({
  tabularData,
  onAddToApp,
}: {
  tabularData: ChatTabularData;
  onAddToApp?: () => void;
}) {
  const { t } = useTranslation("chat");
  const { columns, rows, truncated, row_count: rowCount } = tabularData;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {onAddToApp ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Tooltip title={t("response.tables.addToApp")}>
            <IconButton
              size="small"
              aria-label={t("response.tables.addToApp")}
              onClick={onAddToApp}
              sx={{ color: "var(--theme-text-secondary)" }}
            >
              <DashboardCustomizeOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column} sx={{ fontWeight: 600 }}>
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={`row-${rowIndex}`}>
                {columns.map((column) => (
                  <TableCell key={`${rowIndex}-${column}`}>
                    {formatCellValue(row[column])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Typography variant="caption" sx={{ color: "var(--theme-text-secondary)" }}>
        {rowCount ?? rows.length} row(s)
        {truncated ? " (truncated)" : ""}
      </Typography>
    </Box>
  );
}

function SqlQueriesPanel({
  queries,
  domainId,
}: {
  queries: GeneratedQuery[];
  domainId?: number | null;
}) {
  const { t } = useTranslation("chat");
  const dialect = useSqlDialect(domainId);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {queries.map((query, index) => (
        <Box key={`query-${index}`}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {query.purpose || `Query ${index + 1}`}
            </Typography>
            {query.cross_table_metric ? (
              <Tooltip
                title={
                  query.source_tables && query.source_tables.length > 0
                    ? t("response.sql.multiTableTooltip", {
                        tables: query.source_tables.join(", "),
                      })
                    : t("response.sql.multiTableTooltipGeneric")
                }
              >
                <Chip
                  size="small"
                  label={t("response.sql.multiTableBadge")}
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    bgcolor: "var(--theme-surface-hover)",
                    color: "var(--theme-text-secondary)",
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              </Tooltip>
            ) : null}
          </Box>
          <Typography variant="caption" sx={{ color: "var(--theme-text-secondary)" }}>
            Status: {query.status || "unknown"}
            {typeof query.row_count === "number" ? ` · ${query.row_count} rows` : ""}
            {typeof query.execution_ms === "number" ? ` · ${query.execution_ms} ms` : ""}
          </Typography>
          {query.sql ? (
            <Box sx={{ mt: 1 }}>
              <SqlCodeMirror
                value={query.sql}
                readOnly
                maxHeight="200px"
                dialect={dialect}
              />
            </Box>
          ) : null}
          {query.error_message ? (
            <Typography variant="body2" sx={{ color: "var(--theme-error)", mt: 0.5 }}>
              {query.error_message}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

function AvatarIcon() {
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "var(--theme-surface-hover)",
        color: "var(--theme-text-secondary)",
        flexShrink: 0,
      }}
      aria-hidden
    >
      <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
    </Box>
  );
}

function MessageBubble({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        justifyContent: "flex-start",
      }}
    >
      <Box
        sx={{
          borderRadius: 2,
          px: 2,
          py: 1.25,
          maxWidth: "72%",
          bgcolor: "var(--theme-surface-hover)",
          color: "var(--theme-text-primary)",
          border: "1px solid var(--theme-border)",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function StreamingCursor() {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: 6,
        height: 14,
        ml: 0.25,
        bgcolor: "var(--theme-primary)",
        verticalAlign: "text-bottom",
        animation: "blink 1s step-end infinite",
        "@keyframes blink": {
          "50%": { opacity: 0 },
        },
      }}
      aria-hidden
    />
  );
}
